import { z } from "zod";
import type { DataflowLastRun } from "./dataflows";

/**
 * Connector administration (FR-30 to FR-33): upsert a definition from its seed document,
 * change the run tunables, and manage image tags. Nothing here carries a credential — a
 * definition describes HOW to validate a credential, never a value.
 *
 * The seed document keeps the V2 seed's own field names (`isPreviewEnabled`,
 * `connectorImages`, `connectorImageName`) so one file can be posted to this route or fed to
 * `npm run seed:connectors` unchanged. The `jsonb` blocks are typed `unknown` HERE and stored
 * verbatim, because this schema is also the request parser and must not strip or reorder a key
 * the form reads (the checksum is over the parsed document). Their shapes are checked by
 * `validateSeed` in `shared/connectors/seedValidator.ts`, which every door runs (A13).
 */

export const PIPELINE_TEMPLATES = ["standard", "incremental", "sql_jdbc", "event_drain"] as const;
export type PipelineTemplate = (typeof PIPELINE_TEMPLATES)[number];

/**
 * How the runner starts a connector's container (platform 0018, GUIDELINES §9). The definition
 * DECLARES it; nothing infers it from the connector's name.
 *
 * - `sql_extract`: `python -m sql_extract.run`, the SQL images (`--connector-type` is the flavour).
 * - `skydata_runtime`: `python -m skydata_runtime.run`, the skyConnect package every new
 *   connector exposes to run on the SaaS; identity flags only.
 * - `legacy_script`: `python /app/source-<name>/sync.py` with the legacy platform's flags, kept
 *   only for definitions that still declare it. A seed that declares nothing gets this.
 */
export const CONNECTOR_RUNTIMES = ["sql_extract", "skydata_runtime", "legacy_script"] as const;
export type ConnectorRuntime = (typeof CONNECTOR_RUNTIMES)[number];
/** What a definition gets when its seed declares no runtime (and every row 0018 found). */
export const DEFAULT_CONNECTOR_RUNTIME: ConnectorRuntime = "legacy_script";

/** FR-32: a definition carries at most five tags, exactly one of them the default. */
export const MAX_CONNECTOR_VERSIONS = 5;

/**
 * The words the runner's own per-tenant jobs take in the connector slot of `job-<connector>-<k>`:
 * the dbt job (`job-dbt-<k>`) and the two access probes (#766: `job-access-probe-<k>` as the
 * connector identity, `job-access-probe-dbt-<k>` as the dbt identity, #781: `job-access-probe-push-<k>` as the push identity). A connector with one of
 * these names would share a job, and an identity, with them, so a seed may not use one.
 */
export const DBT_JOB_CONNECTOR = "dbt";
export const ACCESS_PROBE_JOB_CONNECTOR = "access-probe";
export const ACCESS_PROBE_DBT_JOB_CONNECTOR = "access-probe-dbt";
/** #781: the probe that runs as the push identity, `job-access-probe-push-<k>`. */
export const ACCESS_PROBE_PUSH_JOB_CONNECTOR = "access-probe-push";
/** #514 (ADR 0013): the Snowflake push job, `job-skydata-push-<k>`, as the push identity. */
export const PUSH_JOB_CONNECTOR = "skydata-push";
export const RESERVED_CONNECTOR_NAMES: readonly string[] = [
  DBT_JOB_CONNECTOR,
  ACCESS_PROBE_JOB_CONNECTOR,
  ACCESS_PROBE_DBT_JOB_CONNECTOR,
  ACCESS_PROBE_PUSH_JOB_CONNECTOR,
  PUSH_JOB_CONNECTOR,
];

export const connectorNameSchema = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-z0-9][a-z0-9_-]*$/i, "A connector name is letters, digits, hyphen or underscore.");

export const connectorAdminParamsSchema = z.object({ name: connectorNameSchema });
export type ConnectorAdminParams = z.infer<typeof connectorAdminParamsSchema>;

export const connectorVersionParamsSchema = z.object({
  name: connectorNameSchema,
  tag: z.string().min(1).max(128),
});
export type ConnectorVersionParams = z.infer<typeof connectorVersionParamsSchema>;

const seedImageSchema = z.object({
  tag: z.string().min(1).max(128),
  label: z.string().max(200).optional(),
});

const seedEntitySchema = z.object({
  name: z.string().min(1).max(200),
  displayName: z.string().max(200).optional(),
  mergeConfig: z.unknown().optional(),
  schema: z.unknown().optional(),
});

/** Run tunables (FR-30). Absent values fall back to the seed script's own defaults. */
export const connectorTunablesSchema = z.object({
  pipelineTemplate: z.enum(PIPELINE_TEMPLATES).optional(),
  runTimeoutSeconds: z.number().int().min(60).max(172_800).optional(),
  pollIntervalSeconds: z.number().int().min(5).max(3600).optional(),
  cpu: z.number().min(0.25).max(16).optional(),
  memoryGb: z.number().min(0.5).max(128).optional(),
  maxConcurrentRuns: z.number().int().min(1).max(100).optional(),
  /** FR-72: the hosts the connector image may reach; empty means unrestricted. */
  egressFqdns: z.array(z.string().min(1).max(253)).max(100).optional(),
});
export type ConnectorTunables = z.infer<typeof connectorTunablesSchema>;

/**
 * A connector icon: a site-relative path into the icons this build ships, or an
 * absolute https address. Checked for its SHAPE and not only its length.
 *
 * The value is rendered straight into an image source on every screen that lists
 * connectors, so `data:` would let whoever can upsert a definition put arbitrary
 * bytes behind an `<img>`, and `http:` is a mixed-content block in a page served
 * over TLS. Nothing fetches it server-side, so this is not an SSRF guard — it is
 * about what the browser is told to load.
 *
 * **The relative form is what the seeds use**, and it is the reason this is no
 * longer https-only. The logos are committed under `client/public/connector-logos/`
 * and served by the web app itself (`scripts/fetch-connector-logos.ts`), so a
 * shipped connector's icon comes out of the same image as its JavaScript and
 * depends on no storage account at all. It used to point at the V1 platform's
 * public blob container, and the natural replacement — the same public container
 * on this build's own account — is refused by a management-group Azure Policy
 * that denies `allowBlobPublicAccess`.
 *
 * The pattern is narrow on purpose, and each half of it is load-bearing. A single
 * leading slash followed by a literal `connector-logos` segment means a
 * protocol-relative `//evil.example/x.svg` — which a browser resolves as an
 * absolute URL to another origin — cannot match. The name charset excludes `.`
 * and `/`, so no `..` can climb out of the directory. And only `.svg` and `.png`
 * are accepted, which are the two formats `fetch-connector-logos.ts` will write.
 *
 * https stays legal for a definition someone uploads through the admin API that
 * points at a vendor's own CDN, which is a real case this does not need to break.
 */
const RELATIVE_ICON_PATH = /^\/connector-logos\/[a-z0-9-]+\.(svg|png)$/;

const iconUrlSchema = z
  .string()
  .max(2048)
  .refine(
    (value) => RELATIVE_ICON_PATH.test(value) || /^https:\/\//i.test(value),
    "An icon URL must be an https address or a /connector-logos/<name>.svg|png path.",
  );

export const connectorSeedSchema = connectorTunablesSchema.extend({
  name: connectorNameSchema.refine(
    (name) => !RESERVED_CONNECTOR_NAMES.includes(name.toLowerCase()),
    "This connector name is reserved for the platform's own jobs (dbt, access-probe, access-probe-dbt, access-probe-push).",
  ),
  displayName: z.string().min(1).max(200),
  description: z.string().max(4000).optional(),
  kind: z.string().max(50).optional(),
  categories: z.array(z.string().max(100)).max(50).optional(),
  iconUrl: iconUrlSchema.optional(),
  version: z.string().max(32).optional(),
  isActive: z.boolean().optional(),
  isPreviewEnabled: z.boolean().optional(),
  isIncrementalSupported: z.boolean().optional(),
  incrementalGoBackDays: z.number().int().min(0).max(3650).optional(),
  connectorImageName: z.string().max(200).optional(),
  /**
   * How the runner starts the container. Seed-owned and read-only everywhere else: no route
   * edits it, because it is a fact of the image the seed pins, not a tunable.
   */
  runtime: z.enum(CONNECTOR_RUNTIMES).optional(),
  connectorImages: z.array(seedImageSchema).max(MAX_CONNECTOR_VERSIONS).optional(),
  entities: z.array(seedEntitySchema).max(500).optional(),
  fieldGroups: z.array(z.unknown()).optional(),
  fieldDefinitions: z.array(z.unknown()).optional(),
  credentialValidation: z.unknown().optional(),
  entitySelection: z.unknown().optional(),
  ui: z.unknown().optional(),
});
export type ConnectorSeedDocument = z.infer<typeof connectorSeedSchema>;

export const connectorUpsertBodySchema = z.object({
  seed: connectorSeedSchema,
  /** The `--force` of the seed script: apply even when the row drifted in the database. */
  force: z.boolean().default(false),
  /** Classify and report without writing, exactly as `--dry-run` does. */
  dryRun: z.boolean().default(false),
});
export type ConnectorUpsertBody = z.infer<typeof connectorUpsertBodySchema>;

/** The seed script's classification, reused verbatim so both paths report the same words. */
export const SEED_ACTIONS = ["insert", "update", "skip_no_changes", "skip_drift"] as const;
export type SeedAction = (typeof SEED_ACTIONS)[number];

/**
 * What `validateSeed` (`shared/connectors/seedValidator.ts`) found in a seed document (A13).
 *
 * `field` is a dot path into the DOCUMENT (`fieldDefinitions.3.defaultValue`), so the same
 * finding reads the same in the upload dialog, the CLI table and the catalog test; the upload
 * route prefixes `seed.` because that is where the document sits in its body. `issue` names
 * identifiers and counts only (SEC-17): never a default value, a pattern or a credential.
 */
export const SEED_FINDING_RULES = ["schema", "SEC-2", "FR-41", "FR-32", "form"] as const;
export type SeedFindingRule = (typeof SEED_FINDING_RULES)[number];

export const seedFindingSchema = z.object({
  severity: z.enum(["error", "warning"]),
  rule: z.enum(SEED_FINDING_RULES),
  field: z.string(),
  issue: z.string(),
});
export type SeedFinding = z.infer<typeof seedFindingSchema>;

/** A warning as the upload route returns it: the document was accepted, and this is why to look. */
export const seedWarningSchema = seedFindingSchema.omit({ severity: true });
export type SeedWarning = z.infer<typeof seedWarningSchema>;

export const connectorUpsertResultSchema = z.object({
  name: z.string(),
  action: z.enum(SEED_ACTIONS),
  applied: z.boolean(),
  checksum: z.string(),
  /** Set on `skip_drift` when the database holds tags the seed document does not. */
  extraDbTags: z.array(z.string()).optional(),
  detail: z.string(),
  /** Findings that did not refuse the document (A13); absent when there are none. */
  warnings: z.array(seedWarningSchema).optional(),
});
export type ConnectorUpsertResult = z.infer<typeof connectorUpsertResultSchema>;

/** A tunables-only update; the seed blocks are changed by an upsert, never here. */
export const connectorUpdateBodySchema = connectorTunablesSchema
  .extend({
    displayName: z.string().min(1).max(200).optional(),
    description: z.string().max(4000).nullable().optional(),
    iconUrl: iconUrlSchema.nullable().optional(),
    categories: z.array(z.string().max(100)).max(50).optional(),
    isPreview: z.boolean().optional(),
    isIncrementalSupported: z.boolean().optional(),
    incrementalGoBackDays: z.number().int().min(0).max(3650).nullable().optional(),
  })
  .refine((body) => Object.keys(body).length > 0, "Send at least one field to change.");
export type ConnectorUpdateBody = z.infer<typeof connectorUpdateBodySchema>;

export const connectorVersionBodySchema = z.object({
  tag: z.string().min(1).max(128),
  label: z.string().max(200).optional(),
  /** Making a tag the default demotes the previous one in the same transaction. */
  isDefault: z.boolean().default(false),
  releasedAt: z.iso.datetime().optional(),
  notes: z.string().max(4000).optional(),
});
export type ConnectorVersionBody = z.infer<typeof connectorVersionBodySchema>;

export const connectorAdminVersionSchema = z.object({
  tag: z.string(),
  label: z.string().nullable(),
  isDefault: z.boolean(),
  releasedAt: z.string().nullable(),
  notes: z.string().nullable(),
});
export type ConnectorAdminVersionView = z.infer<typeof connectorAdminVersionSchema>;

/** The admin view of a definition: tunables included, seed blocks excluded (they are large). */
export const connectorAdminViewSchema = z.object({
  name: z.string(),
  displayName: z.string(),
  description: z.string().nullable(),
  kind: z.string(),
  categories: z.array(z.string()),
  iconUrl: z.string().nullable(),
  version: z.string(),
  isActive: z.boolean(),
  isPreview: z.boolean(),
  imageName: z.string().nullable(),
  pipelineTemplate: z.enum(PIPELINE_TEMPLATES),
  /** Read-only: the seed declares it (platform 0018). */
  runtime: z.enum(CONNECTOR_RUNTIMES),
  runTimeoutSeconds: z.number(),
  pollIntervalSeconds: z.number(),
  cpu: z.number(),
  memoryGb: z.number(),
  maxConcurrentRuns: z.number(),
  isIncrementalSupported: z.boolean(),
  incrementalGoBackDays: z.number().nullable(),
  supportsIncrementalPerEntity: z.boolean(),
  egressFqdns: z.array(z.string()),
  seedChecksum: z.string().nullable(),
  entityCount: z.number(),
  versions: z.array(connectorAdminVersionSchema),
  updatedAt: z.string(),
  /** #2478: who last changed it, as a label (name, masked address, "Removed user", "System"), never an id. */
  updatedByName: z.string().nullable(),
  /**
   * #2478: when a release's seed changed the definition after the person named in `updatedByName`
   * did; null when the seed has not changed it since, or the last change was the seed's own.
   */
  releaseUpdatedAt: z.string().nullable(),
});
export type ConnectorAdminView = z.infer<typeof connectorAdminViewSchema>;

export const connectorAdminQuerySchema = z.object({
  includeInactive: z
    .enum(["true", "false"])
    .default("true")
    .transform((v) => v === "true"),
});
export type ConnectorAdminQuery = z.infer<typeof connectorAdminQuerySchema>;

/**
 * Round 10 wave 2 (A3 / B-27): where one connector is used, across every instance. The usage
 * view is a platform read (`platform.connectors.manage`); a row names a connection, never a
 * credential or a field value. `unreadable` lists instances whose store could not be read: a
 * Deactivate fails closed while any is listed.
 */
export interface ConnectorUsageRow {
  tenantId: string;
  tenantName: string;
  instanceId: string;
  instanceName: string;
  connectionId: string;
  connectionName: string;
  /** The connection's own `image_tag`, or "default (<tag>)" when it follows the default. */
  image: string;
  state: string;
  lastRunStatus: string | null;
  lastRunAt: string | null;
  /**
   * #993: what the Connections list reads to name the connection's status (`connectionStatusOf`
   * in the client), from the same source as `DataflowSummary`: the runner's flags, the run
   * holding it now, and its last run with this connection's own outcome and any wait.
   */
  isProcessing: boolean;
  isFailed: boolean;
  lastRunId: string | null;
  lastRefreshAt: string | null;
  /** #1372: the connection's selected tables, the number its own page and list show. */
  selectedEntityCount: number;
  /**
   * The run holding the connection now, null when none does. Absent when the instance's run
   * facts could not be read (#2094 review): "not known" is not "idle", so the client's status
   * then falls back to `isProcessing` (`isListedRunning`).
   */
  activeRunId?: string | null;
  lastRun?: DataflowLastRun;
  /** `/i/<instance id>/connections/<id>`: the web path, the client adds the origin. */
  path: string;
}

export interface ConnectorUsageUnreadable {
  tenantId: string;
  instanceId: string;
  instanceName: string;
}

export interface ConnectorUsageView {
  connector: string;
  connections: number;
  instances: number;
  rows: ConnectorUsageRow[];
  unreadable: ConnectorUsageUnreadable[];
  /** Disabled instances whose database or schema no longer exists; they do not block. */
  unavailable: ConnectorUsageUnreadable[];
}
